// Storefront commerce blocks — `cart_checkout` and `my_records`.
//
// Shared by every live surface (the in-app preview, the Run view, the site
// viewer); the host supplies the server through `PageDataBlocksContext`
// (commerceApi.ts). Both are LIVE-ONLY: the static prerender skips a page
// holding one (`pageNeedsLiveRuntime` in page-schema) and the hosted site
// serves it from the live app instead.
//
// What the browser sends is deliberately thin: a checkout posts the shopper's
// typed values keyed by the block's FIELD keys plus the basket's sku/qty —
// the server reads the order object, the column mapping and every fixed value
// from the saved page, and the order's own business logic prices it. The
// total shown afterwards is the one the server SAVED.
import React, { Fragment, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { CartCheckoutBlock, CheckoutField, CheckoutPromo, MyRecordsBlock, SavedRecordsBlock, SavedRecordsField } from '@twasta/page-schema'

import { substituteVars } from './actions/substitute'
import {
  addToCart, cartLineTotal, cartTotals, formatMoney, readCart, writeCart, type CartLine,
} from './cart'
import {
  COMMERCE_SIGN_IN_CODES, CommerceApiError, PageDataBlocksContext, notifyRecordsChanged,
  type CheckoutBootstrap, type MyRecordsResult, type RecordSummary, type PageDataBlocksApi,
  type PlaceOrderResult, type PromoCheck, type SavedAddress, type SavedRecord, type SavedRecordsResult,
} from './commerceApi'
import { SignInPromptCard, type SignInCode } from './signInPrompt'
import { PageIcon } from './iconMap'

export { PageDataBlocksContext, CommerceApiError } from './commerceApi'
export type { PageDataBlocksApi } from './commerceApi'

const MUTED = 'var(--pg-text-secondary, #64748b)'
const BORDER = '1px solid var(--pg-border, #e2e8f0)'
const CARD: React.CSSProperties = {
  border: BORDER, borderRadius: 'var(--pg-radius, 12px)', background: 'var(--pg-surface, #fff)',
  padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
}
const INPUT: React.CSSProperties = {
  padding: '9px 12px', fontSize: '0.92rem', width: '100%', boxSizing: 'border-box',
  border: BORDER, borderRadius: 8, background: 'var(--pg-surface, #fff)',
  color: 'var(--pg-text, inherit)', fontFamily: 'inherit',
}
/** Forms beside the order summary; the summary drops under them when narrow. */
const LAYOUT: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 24, alignItems: 'start',
}
const PRIMARY_BTN: React.CSSProperties = {
  padding: '11px 20px', fontWeight: 600, border: 'none', borderRadius: 'var(--pg-button-radius, 8px)',
  background: 'var(--pg-primary, #2563eb)', color: 'var(--pg-on-primary, #fff)', cursor: 'pointer',
  fontSize: '0.95rem',
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback
}

function SignInSlot({ api, code, message }: { api: PageDataBlocksApi | null; code: string; message: string }) {
  if (api?.renderSignIn) return <>{api.renderSignIn(code, message)}</>
  return <SignInPromptCard refusal={{ code: code as SignInCode, message }} />
}

function isSignInError(err: unknown): err is CommerceApiError {
  return err instanceof CommerceApiError && COMMERCE_SIGN_IN_CODES.includes(err.code)
}

function newCheckoutId(): string {
  try {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
    if (c?.randomUUID) return c.randomUUID().replace(/-/g, '')
  } catch { /* fall through */ }
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 12)
}

function useCartLines(): CartLine[] {
  const [lines, setLines] = useState<CartLine[]>(() => (typeof window === 'undefined' ? [] : readCart()))
  useEffect(() => {
    const on = () => setLines(readCart())
    window.addEventListener('pg:cart', on)
    window.addEventListener('storage', on)
    on()
    return () => {
      window.removeEventListener('pg:cart', on)
      window.removeEventListener('storage', on)
    }
  }, [])
  return lines
}

// ── cart_checkout ─────────────────────────────────────────────────────────

const SAMPLE_LINES: CartLine[] = [
  { sku: 'SAMPLE-1', name: 'Sample item', price: 1200, prefix: '₹', qty: 2 },
  { sku: 'SAMPLE-2', name: 'Another item', price: 650, prefix: '₹', qty: 1 },
]

function fieldGroup(f: CheckoutField): 'contact' | 'address' {
  if (f.group) return f.group
  return f.kind === 'email' || f.kind === 'tel' || f.prefill ? 'contact' : 'address'
}

function FieldInput({ f, value, error, onChange }: {
  f: CheckoutField; value: string; error?: string; onChange: (v: string) => void
}) {
  const id = `pg-co-${f.key}`
  const common = {
    id, name: f.key, value, required: !!f.required, maxLength: f.maxLength || 400,
    autoComplete: f.autocomplete, 'aria-invalid': error ? true : undefined,
    style: { ...INPUT, ...(error ? { borderColor: 'var(--pg-error, #dc2626)' } : {}) },
  }
  return (
    <label htmlFor={id} style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.85rem',
                                 gridColumn: f.kind === 'textarea' ? '1 / -1' : undefined }}>
      <span>{f.label}{f.required ? ' *' : ''}</span>
      {f.kind === 'textarea'
        ? <textarea {...common} rows={3} onChange={(e) => onChange(e.currentTarget.value)} />
        : <input {...common}
                 type={f.kind === 'email' ? 'email' : f.kind === 'tel' ? 'tel' : 'text'}
                 inputMode={f.kind === 'postcode' ? 'numeric' : undefined}
                 onChange={(e) => onChange(e.currentTarget.value)} />}
      {error && <span role="alert" style={{ color: 'var(--pg-error, #dc2626)', fontSize: '0.8rem' }}>{error}</span>}
    </label>
  )
}

/** The promo code's field key — the server reserves it (page_commerce.PROMO_KEY). */
const PROMO_KEY = '_promo'

/** `{{code}}` / `{{label}}` / `{{percent}}` into a promo text. A template that
 *  names `{{label}}` falls back to `fallback` when the code has no label. */
function promoText(tpl: string | undefined, fallback: string, p: PromoCheck): string {
  let t = tpl || fallback
  if (!p.label && t.indexOf('{{label}}') !== -1) t = fallback
  const pct = p.percent == null ? '' : String(Math.round(p.percent * 100) / 100)
  return t.replace(/\{\{\s*(code|label|percent)\s*\}\}/g, (_m, k: string) =>
    k === 'code' ? p.code : k === 'label' ? (p.label || '') : pct)
}

function PromoBox({ promo, applied, input, busy, error, onInput, onApply, onRemove, disabled }: {
  promo: CheckoutPromo; applied: PromoCheck | null; input: string; busy: boolean; error: string
  onInput: (v: string) => void; onApply: () => void; onRemove: () => void; disabled?: boolean
}) {
  const id = 'pg-co-promo'
  return (
    <div style={{ borderTop: BORDER, paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}
         data-pg-checkout-promo="">
      <label htmlFor={id} style={{ fontSize: '0.85rem', fontWeight: 600 }}>{promo.label || 'Discount code'}</label>
      {applied ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                      padding: '8px 10px', borderRadius: 8, fontSize: '0.85rem',
                      background: 'var(--pg-success-bg, rgba(22,163,74,.1))', color: 'var(--pg-success, #15803d)' }}>
          <span role="status">✓ {promoText(promo.appliedText, '{{code}} applied', applied)}</span>
          <button type="button" onClick={onRemove}
                  style={{ border: 'none', background: 'transparent', color: 'inherit', fontWeight: 600,
                           cursor: 'pointer', fontSize: '0.82rem', minHeight: 32 }}>Remove</button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8 }}>
          <input id={id} style={{ ...INPUT, textTransform: 'uppercase' }} value={input} maxLength={40}
                 placeholder={promo.placeholder || ''} autoComplete="off" disabled={disabled}
                 aria-invalid={error ? true : undefined}
                 onChange={(e) => onInput(e.currentTarget.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onApply() } }} />
          <button type="button" onClick={onApply} disabled={disabled || busy || !input.trim()}
                  style={{ ...PRIMARY_BTN, padding: '9px 16px', opacity: busy ? 0.7 : 1, flex: 'none' }}>
            {busy ? 'Checking…' : 'Apply'}
          </button>
        </div>
      )}
      {error && <span role="alert" style={{ color: 'var(--pg-error, #dc2626)', fontSize: '0.8rem' }}>{error}</span>}
    </div>
  )
}

function CheckoutNote({ note }: { note: NonNullable<CartCheckoutBlock['note']> }) {
  if (!note.title && !note.body) return null
  return (
    <div data-pg-checkout-note="" style={{
      display: 'flex', gap: 14, alignItems: 'center', padding: '16px 20px',
      borderRadius: 'var(--pg-radius, 12px)', background: 'var(--pg-info-bg, var(--pg-surface-muted, rgba(37,99,235,.08)))',
    }}>
      {note.icon && (
        <span aria-hidden style={{
          width: 44, height: 44, flexShrink: 0, borderRadius: 10, display: 'inline-flex', alignItems: 'center',
          justifyContent: 'center', background: 'var(--pg-surface, #fff)', color: 'var(--pg-primary, #2563eb)',
        }}><PageIcon name={note.icon} size={22} /></span>
      )}
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '0.95rem' }}>
        {note.title && <strong style={{ color: 'var(--pg-heading, inherit)' }}>{note.title}</strong>}
        {note.body && <span>{note.body}</span>}
      </span>
    </div>
  )
}

function SummaryLine({ l, fallbackPrefix }: { l: CartLine; fallbackPrefix: string }) {
  const prefix = l.prefix || fallbackPrefix
  const each = l.units && l.units > 1 ? `${l.qty} × ${l.units}` : String(l.qty)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.9rem' }} data-pg-checkout-line="">
      {l.image && (
        <img src={l.image} alt="" loading="lazy"
             style={{ width: 52, height: 52, objectFit: 'contain', flexShrink: 0, borderRadius: 8, border: BORDER,
                      background: 'var(--pg-surface, #fff)' }} />
      )}
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontWeight: 600 }}>{l.name}</span>
        <span style={{ color: MUTED, fontSize: '0.82rem' }}>Qty {each} · {formatMoney(l.price, prefix)} each</span>
      </span>
      <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{formatMoney(cartLineTotal(l), prefix)}</span>
    </div>
  )
}

function Summary({ lines, block, applied, promoSlot, footer }: {
  lines: CartLine[]; block: CartCheckoutBlock; applied?: PromoCheck | null; promoSlot?: React.ReactNode
  /** Rendered at the foot of the card — the submit button when `submitInSummary`. */
  footer?: React.ReactNode
}) {
  const base = cartTotals(lines, block.summaryRules || {})
  const rules = block.summaryRules || {}
  // An applied code comes off each line before tax — an ESTIMATE; the order's
  // own logic prices it and the confirmation shows the saved total.
  const pct = applied?.valid && applied.percent ? applied.percent : 0
  const discount = pct ? Math.round(base.subtotal * pct / 100) : 0
  const taxable = base.subtotal - discount
  const tax = rules.taxPercent ? Math.round(taxable * (rules.taxPercent / 100)) : 0
  const t = { ...base, tax, total: taxable + tax + base.shipping }
  return (
    <div style={{ ...CARD, gap: 10 }} data-pg-checkout-summary="">
      <strong style={{ fontSize: '1.1rem', color: 'var(--pg-heading, inherit)' }}>{block.summaryHeading || 'Order summary'}</strong>
      {lines.map((l) => <SummaryLine key={(l.key || l.sku) + (l.start || '')} l={l} fallbackPrefix={t.prefix} />)}
      {/* The code box sits between the lines and the totals it changes. */}
      {promoSlot}
      <div style={{ borderTop: BORDER, paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.9rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Subtotal</span><span>{formatMoney(t.subtotal, t.prefix)}</span></div>
        {discount > 0 && applied ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--pg-success, #15803d)', fontWeight: 600 }}
               data-pg-checkout-discount="">
            <span>{promoText(block.promo?.discountLabel, 'Discount ({{percent}}%)', applied)}</span>
            <span>−{formatMoney(discount, t.prefix)}</span>
          </div>
        ) : null}
        {rules.taxPercent ? (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{rules.taxLabel || `Tax (${rules.taxPercent}%)`}</span><span>{formatMoney(t.tax, t.prefix)}</span></div>
        ) : null}
        {rules.shippingFlat ? (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{rules.shippingLabel || 'Shipping'}</span>
            <span>{t.shipping ? formatMoney(t.shipping, t.prefix) : (rules.freeLabel || 'Free')}</span></div>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1rem' }}><span>{block.summaryTotalLabel || 'Estimated total'}</span><span>{formatMoney(t.total, t.prefix)}</span></div>
      </div>
      {footer}
      <span style={{ fontSize: '0.78rem', color: MUTED, textAlign: footer ? 'center' : undefined }}>
        {block.summaryNote ?? 'Prices, tax and delivery are confirmed when the order is placed.'}
      </span>
    </div>
  )
}

export function CartCheckoutRenderer({ block, setVar, navigate, design = false }: {
  block: CartCheckoutBlock
  /** Writes `orderIdVar` (read by a `payment_checkout`'s `txnIdVar`). */
  setVar?: (name: string, value: unknown) => void
  /** Host navigation for `onSuccessNavigateTo`; default `location.assign`. */
  navigate?: (href: string) => void
  design?: boolean
}) {
  const api = useContext(PageDataBlocksContext)
  const cart = useCartLines()
  const lines = design ? SAMPLE_LINES : cart
  const fields = useMemo(() => (block.fields || []).filter((f) => f && f.key), [block.fields])
  const [boot, setBoot] = useState<CheckoutBootstrap | null>(null)
  const [bootErr, setBootErr] = useState<CommerceApiError | Error | null>(null)
  const [values, setValues] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [chosen, setChosen] = useState<string>('')        // saved address id, '' = new
  const [saveAddr, setSaveAddr] = useState(true)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [signIn, setSignIn] = useState<{ code: string; message: string } | null>(null)
  const [done, setDone] = useState<PlaceOrderResult | null>(null)
  // The basket's currency, kept from submit: the basket is emptied on order.
  const [donePrefix, setDonePrefix] = useState('')
  const [promoInput, setPromoInput] = useState('')
  const [promoApplied, setPromoApplied] = useState<PromoCheck | null>(null)
  const [promoBusy, setPromoBusy] = useState(false)
  // Bumped after the shopper signs in through the identify step, so the
  // bootstrap runs again (saved addresses + identity prefill).
  const [bootNonce, setBootNonce] = useState(0)
  // `signedInContact: 'card'` — the shopper pressed Change to edit the fields.
  const [editContact, setEditContact] = useState(false)
  const checkoutId = useRef(newCheckoutId())
  const live = !design && !!api

  useEffect(() => {
    if (!live) return
    let gone = false
    api!.checkoutBootstrap(block.id).then((b) => {
      if (gone) return
      setBoot(b)
      // Identity prefill, then the first saved address.
      setValues((prev) => {
        const next = { ...prev }
        for (const f of fields) {
          if (next[f.key]) continue
          if (f.prefill === 'name' && b.me?.name) next[f.key] = b.me.name
          if (f.prefill === 'email' && b.me?.email) next[f.key] = b.me.email
          if (f.prefill === 'phone' && b.me?.phone) next[f.key] = b.me.phone
        }
        const first = b.addresses?.[0]
        if (first) for (const [k, v] of Object.entries(first.values)) if (!next[k]) next[k] = v
        return next
      })
      if (b.addresses?.[0]) setChosen(b.addresses[0].id)
    }).catch((err) => { if (!gone) setBootErr(err) })
    return () => { gone = true }
  }, [live, api, block.id, fields, bootNonce])

  const onSignedIn = useCallback(() => {
    setSignIn(null)
    setBootErr(null)
    setBoot(null)
    setBootNonce((n) => n + 1)
  }, [])
  // Every order belongs to an account: when one is needed and missing the
  // HOST's identify-first step renders here (email / mobile → sign in or
  // create the account), else the shared sign-in card.
  // The basket stays in view beside it: a shopper asked who they are should
  // still see what they are about to order.
  const needAccount = (code: string, message: string) => {
    const withSummary = lines.length > 0 && block.showSummary !== false
    const step = api?.renderIdentify
      ? (withSummary
        // Framed like the contact card it stands in for, filling its column.
        ? <div style={CARD}>{api.renderIdentify({
            onSignedIn, reason: code, message, bare: true,
            labels: { heading: block.contactHeading || 'Your details' },
          })}</div>
        : <>{api.renderIdentify({ onSignedIn, reason: code, message })}</>)
      : <SignInSlot api={api} code={code} message={message} />
    if (!withSummary) return step
    return (
      <div data-pg-checkout="" style={LAYOUT}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {step}
          {block.note && <CheckoutNote note={block.note} />}
        </div>
        {summaryCard(null)}
      </div>
    )
  }

  const pickAddress = (a: SavedAddress | null) => {
    setChosen(a ? a.id : '')
    setValues((prev) => {
      const next = { ...prev }
      for (const f of fields) {
        if (!f.addressColumn) continue
        next[f.key] = a ? (a.values[f.key] || '') : ''
      }
      return next
    })
  }

  const columnToKey = useMemo(() => {
    const m: Record<string, string> = {}
    for (const f of fields) if (f.column) m[f.column.toLowerCase()] = f.key
    return m
  }, [fields])

  const submit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!live || busy) return
    const missing: Record<string, string> = {}
    for (const f of fields) {
      if (f.required && !(values[f.key] || '').trim()) missing[f.key] = `${f.label} is required`
    }
    setErrors(missing)
    if (Object.keys(missing).length) { setMessage('Please fill in the highlighted fields.'); return }
    const current = readCart()
    if (!current.length) { setMessage(block.emptyText || 'Your basket is empty.'); return }
    setBusy(true)
    setMessage('')
    try {
      const code = block.promo
        ? (promoApplied?.code || (api!.checkPromo ? '' : promoInput.trim().toUpperCase()))
        : ''
      const res = await api!.placeOrder(block.id, {
        values: code ? { ...values, [PROMO_KEY]: code } : values,
        lines: current.map((l) => ({ sku: l.sku, qty: l.qty, start: l.start, end: l.end, units: l.units })),
        save_address: !!boot?.signed_in && block.addresses?.allowSave !== false && !chosen && saveAddr,
        checkout_id: checkoutId.current,
      }, (block as { _embedToken?: string })._embedToken)
      if (block.orderIdVar && res.txn_id) setVar?.(block.orderIdVar, res.txn_id)
      setDonePrefix(current[0]?.prefix || '')
      if ((block.clearCart || 'on_order') === 'on_order') writeCart([])
      checkoutId.current = newCheckoutId()
      setDone(res)
      if (block.onSuccessNavigateTo) {
        const href = substituteVars(block.onSuccessNavigateTo, {
          id: res.txn_id || '', reference: res.reference == null ? '' : String(res.reference),
        })
        if (navigate) navigate(href)
        else if (typeof window !== 'undefined') window.location.assign(href)
      }
    } catch (err) {
      if (isSignInError(err)) {
        setSignIn({ code: err.code, message: err.message })
      } else if (err instanceof CommerceApiError && err.details?.errors?.length) {
        const fe: Record<string, string> = {}
        for (const x of err.details.errors) {
          const key = x.field || (x.column ? columnToKey[String(x.column).toLowerCase()] : '')
          if (key && x.message) fe[key] = x.message
        }
        setErrors(fe)
        setMessage(err.message)
      } else {
        setMessage(messageOf(err, 'Your order could not be placed — please try again.'))
      }
    } finally {
      setBusy(false)
    }
  }, [live, busy, fields, values, block, api, boot, chosen, saveAddr, setVar, navigate, columnToKey,
      promoApplied, promoInput])

  const applyPromo = useCallback(async () => {
    const code = promoInput.trim()
    if (!code || !block.promo) return
    setErrors((p) => ({ ...p, [PROMO_KEY]: '' }))
    if (!live || !api?.checkPromo) {
      // No check door here: the code still travels with the order.
      setPromoApplied(null)
      return
    }
    setPromoBusy(true)
    try {
      const subtotal = cartTotals(readCart(), block.summaryRules || {}).subtotal
      const r = await api.checkPromo(block.id, code, subtotal)
      if (r.valid) setPromoApplied(r)
      else { setPromoApplied(null); setErrors((p) => ({ ...p, [PROMO_KEY]: r.message || 'This code cannot be used.' })) }
    } catch (err) {
      setErrors((p) => ({ ...p, [PROMO_KEY]: messageOf(err, 'The code could not be checked.') }))
    } finally {
      setPromoBusy(false)
    }
  }, [promoInput, block, live, api])

  function summaryCard(footer: React.ReactNode) {
    return (
      <Summary lines={lines} block={block} applied={promoApplied} footer={footer}
               promoSlot={block.promo ? (
                 <PromoBox promo={block.promo} applied={promoApplied} input={promoInput}
                           busy={promoBusy} error={errors[PROMO_KEY] || ''} disabled={design}
                           onInput={(v) => { setPromoInput(v); if (errors[PROMO_KEY]) setErrors((p) => ({ ...p, [PROMO_KEY]: '' })) }}
                           onApply={applyPromo}
                           onRemove={() => { setPromoApplied(null); setPromoInput('') }} />
               ) : null} />
    )
  }

  if (!design && !api) {
    return <div style={{ ...CARD, color: MUTED, fontSize: 13 }}>Checkout is not available here.</div>
  }
  if (signIn) return needAccount(signIn.code, signIn.message)
  if (bootErr) {
    if (isSignInError(bootErr)) return needAccount(bootErr.code, bootErr.message)
    return <div role="alert" style={{ ...CARD, color: 'var(--pg-error, #dc2626)' }}>{messageOf(bootErr, 'Checkout could not be loaded.')}</div>
  }
  if (boot && boot.requires_sign_in && !boot.signed_in) {
    return needAccount('SIGN_IN_REQUIRED', 'Sign in to place your order.')
  }

  if (done) {
    const prefix = donePrefix || lines[0]?.prefix || ''
    const total = done.total === null || done.total === undefined ? null : Number(done.total)
    return (
      <div style={{ ...CARD, alignItems: 'flex-start' }} data-pg-checkout-done="">
        <strong style={{ fontSize: '1.15rem' }}>{block.successTitle || 'Thank you — your order is placed'}</strong>
        {done.reference != null && <span>Order <strong>{String(done.reference)}</strong></span>}
        {total !== null && !Number.isNaN(total) && <span>Total {formatMoney(total, prefix)}</span>}
        {done.pending_approval && <span style={{ color: MUTED }}>It will be confirmed once it has been reviewed.</span>}
        <span style={{ color: MUTED }}>{block.successBody || 'Complete the payment below to confirm it.'}</span>
      </div>
    )
  }

  if (!lines.length) {
    return (
      <div style={{ ...CARD, alignItems: 'center', textAlign: 'center' }} data-pg-checkout-empty="">
        <span>{block.emptyText || 'Your basket is empty.'}</span>
        {block.emptyCtaHref && <a href={block.emptyCtaHref} style={{ color: 'var(--pg-link, var(--pg-primary))', fontWeight: 600 }}>Continue shopping</a>}
      </div>
    )
  }

  const contact = fields.filter((f) => fieldGroup(f) === 'contact')
  const address = fields.filter((f) => fieldGroup(f) === 'address')
  const saved = boot?.addresses || []
  const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }
  const set = (k: string, v: string) => { setValues((p) => ({ ...p, [k]: v })); if (errors[k]) setErrors((p) => ({ ...p, [k]: '' })) }

  // Signed in with every contact field already filled from the account (and
  // none flagged by the server): one line instead of the fields.
  const who = boot?.me?.email || boot?.me?.name || ''
  const contactAsCard = block.signedInContact === 'card' && !!boot?.signed_in && !!who && !editContact
    && contact.every((f) => !f.required || (values[f.key] || '').trim()) && contact.every((f) => !errors[f.key])
  const submitButton = (
    <button type="submit" disabled={busy || design}
            style={{ ...PRIMARY_BTN, ...(block.submitInSummary ? { width: '100%', padding: '14px 20px', borderRadius: 999 } : { alignSelf: 'flex-start' }),
                     opacity: busy ? 0.7 : 1, cursor: busy ? 'wait' : 'pointer' }}>
      {busy ? 'Placing order…' : (block.submitLabel || 'Place order')}
    </button>
  )
  const errorLine = message ? <p role="alert" style={{ margin: 0, color: 'var(--pg-error, #dc2626)', fontSize: '0.9rem' }}>{message}</p> : null

  return (
    <form onSubmit={submit} noValidate data-pg-checkout="" style={LAYOUT}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {block.heading && <h2 style={{ margin: 0, fontSize: '1.35rem' }}>{block.heading}</h2>}
        {contact.length > 0 && (
          <div style={CARD}>
            <strong style={{ fontSize: '1.1rem', color: 'var(--pg-heading, inherit)' }}>{block.contactHeading || 'Contact details'}</strong>
            {contactAsCard ? (
              <div data-pg-checkout-signed-in=""
                   style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 14px',
                            borderRadius: 10, background: 'var(--pg-surface-muted, rgba(100,116,139,.08))', fontSize: '0.92rem' }}>
                <span>Signed in as <strong>{who}</strong></span>
                <button type="button" onClick={() => setEditContact(true)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 600,
                                 color: 'var(--pg-link, var(--pg-primary))', fontSize: '0.9rem' }}>Change</button>
              </div>
            ) : (
              <div style={grid}>
                {contact.map((f) => <FieldInput key={f.key} f={f} value={values[f.key] || ''} error={errors[f.key]} onChange={(v) => set(f.key, v)} />)}
              </div>
            )}
          </div>
        )}
        {address.length > 0 && (
          <div style={CARD}>
            <strong style={{ fontSize: '1.1rem', color: 'var(--pg-heading, inherit)' }}>{block.addressHeading || 'Delivery address'}</strong>
            {saved.length > 0 && (
              <div role="radiogroup" aria-label="Saved addresses" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                {saved.map((a) => (
                  <label key={a.id} style={{ border: chosen === a.id ? '2px solid var(--pg-primary, #2563eb)' : BORDER,
                                             borderRadius: 10, padding: 10, cursor: 'pointer', fontSize: '0.85rem', display: 'flex', gap: 8 }}>
                    <input type="radio" name="pg-co-address" checked={chosen === a.id} onChange={() => pickAddress(a)} />
                    <span>{a.label}</span>
                  </label>
                ))}
                <label style={{ border: chosen === '' ? '2px solid var(--pg-primary, #2563eb)' : BORDER,
                                borderRadius: 10, padding: 10, cursor: 'pointer', fontSize: '0.85rem', display: 'flex', gap: 8 }}>
                  <input type="radio" name="pg-co-address" checked={chosen === ''} onChange={() => pickAddress(null)} />
                  <span>Use a new address</span>
                </label>
              </div>
            )}
            <div style={grid}>
              {address.map((f) => <FieldInput key={f.key} f={f} value={values[f.key] || ''} error={errors[f.key]} onChange={(v) => set(f.key, v)} />)}
            </div>
            {boot?.signed_in && block.addresses?.object && block.addresses.allowSave !== false && chosen === '' && (
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.85rem' }}>
                <input type="checkbox" checked={saveAddr} onChange={(e) => setSaveAddr(e.currentTarget.checked)} />
                Save this address for next time
              </label>
            )}
          </div>
        )}
        {block.note && <CheckoutNote note={block.note} />}
        {/* The button lives in the summary only when that card is shown. */}
        {(!block.submitInSummary || block.showSummary === false) && <>{submitButton}{errorLine}</>}
      </div>
      {block.showSummary !== false && summaryCard(block.submitInSummary ? <>{submitButton}{errorLine}</> : null)}
    </form>
  )
}

// ── my_records ─────────────────────────────────────────────────────────────

const TONES: Record<string, { bg: string; fg: string }> = {
  success: { bg: 'rgba(22,163,74,.12)', fg: 'var(--pg-success, #15803d)' },
  warning: { bg: 'rgba(217,119,6,.14)', fg: 'var(--pg-warning, #b45309)' },
  error: { bg: 'rgba(220,38,38,.12)', fg: 'var(--pg-error, #b91c1c)' },
  info: { bg: 'rgba(37,99,235,.12)', fg: 'var(--pg-primary, #1d4ed8)' },
  neutral: { bg: 'rgba(100,116,139,.14)', fg: MUTED },
}

function Chip({ text, tone }: { text: string; tone?: string }) {
  const t = TONES[tone || 'neutral'] || TONES.neutral
  return (
    <span style={{ background: t.bg, color: t.fg, borderRadius: 999, padding: '2px 10px', fontSize: '0.75rem', fontWeight: 600 }}>
      {text}
    </span>
  )
}

const SAMPLE_ORDERS: RecordSummary[] = [
  { id: 'S1', reference: 'ORD-1042', date: '2026-10-01', status: 'SHIPPED', payment_status: 'PAID', total: 3050,
    lines: [{ sku: 'SAMPLE-1', name: 'Sample item', qty: 2, unit_price: 1200, amount: 2400, image: null },
            { sku: 'SAMPLE-2', name: 'Another item', qty: 1, unit_price: 650, amount: 650, image: null }] },
  { id: 'S2', reference: 'ORD-1031', date: '2026-09-12', status: 'DELIVERED', payment_status: 'PAID', total: 1200,
    lines: [{ sku: 'SAMPLE-1', name: 'Sample item', qty: 1, unit_price: 1200, amount: 1200, image: null }] },
]

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

export function MyRecordsRenderer({ block, navigate, design = false }: {
  block: MyRecordsBlock
  navigate?: (href: string) => void
  design?: boolean
}) {
  const api = useContext(PageDataBlocksContext)
  const live = !design && !!api
  // One order from the URL (a confirmation page) — someone else's id is
  // simply not among the caller's own, so it is not found.
  const record = useMemo(() => {
    if (!block.recordFromQuery || typeof window === 'undefined') return ''
    try { return new URLSearchParams(window.location.search).get(block.recordFromQuery) || '' } catch { return '' }
  }, [block.recordFromQuery])
  const single = !!block.recordFromQuery
  const [data, setData] = useState<MyRecordsResult | null>(design ? { records: SAMPLE_ORDERS, total: 2, offset: 0, limit: 10 } : null)
  const [err, setErr] = useState<unknown>(null)
  const [loading, setLoading] = useState(false)
  const prefix = block.currencyPrefix || ''
  const label = (v: unknown) => {
    const s = v == null ? '' : String(v)
    return block.valueLabels?.[s] || s
  }

  const load = useCallback((offset: number) => {
    if (!live) return
    if (single && !record) { setData({ records: [], total: 0, offset: 0, limit: 1 }); return }
    setLoading(true)
    api!.myRecords(block.id, offset, single ? record : undefined)
      .then((d) => { setData((prev) => (offset > 0 && prev ? { ...d, records: [...prev.records, ...d.records] } : d)); setErr(null) })
      .catch(setErr)
      .finally(() => setLoading(false))
  }, [live, api, block.id, single, record])

  useEffect(() => { load(0) }, [load])

  if (!design && !api) {
    return <div style={{ ...CARD, color: MUTED, fontSize: 13 }}>This list is not available here.</div>
  }
  if (err) {
    if (isSignInError(err)) return <SignInSlot api={api} code={err.code} message={err.message} />
    return <div role="alert" style={{ ...CARD, color: 'var(--pg-error, #dc2626)' }}>{messageOf(err, 'Your records could not be loaded.')}</div>
  }
  if (!data) return <div style={{ ...CARD, color: MUTED }}>{loading ? 'Loading…' : ''}</div>

  const go = (href: string) => { if (navigate) navigate(href); else if (typeof window !== 'undefined') window.location.assign(href) }
  const reorder = block.reorder === false ? null : (block.reorder || null)
  const reorderLines = (o: RecordSummary) => {
    for (const l of o.lines) {
      if (!l.sku) continue
      addToCart({ sku: String(l.sku), name: String(l.name || l.sku), price: num(l.unit_price) || 0,
                  prefix, image: l.image || undefined, qty: Math.max(1, num(l.qty) || 1) })
    }
    if (reorder?.cartHref) go(reorder.cartHref)
  }
  const payFor = (o: RecordSummary) => {
    const p = block.pay
    if (!p?.href) return false
    const ps = String(o.payment_status || '').toUpperCase()
    if (!(p.when || []).map((w) => w.toUpperCase()).includes(ps)) return false
    if ((p.hideWhenStatus || []).map((w) => w.toUpperCase()).includes(String(o.status || '').toUpperCase())) return false
    return true
  }
  const continueLink = block.continueHref ? (
    <a href={block.continueHref}
       style={{ alignSelf: single ? 'flex-start' : 'center', padding: '10px 18px', border: BORDER, borderRadius: 'var(--pg-button-radius, 8px)',
                color: 'inherit', textDecoration: 'none', fontWeight: 600, fontSize: '0.92rem' }}>
      {block.continueLabel || 'Continue shopping'}
    </a>
  ) : null

  const money = (v: unknown) => { const n = num(v); return n === null ? null : formatMoney(n, prefix) }
  const breakdown = (o: RecordSummary) => {
    const rows: { k: string; v: string; strong?: boolean; tone?: string }[] = []
    const sub = money(o.subtotal); if (sub) rows.push({ k: 'Subtotal', v: sub })
    const disc = num(o.discount)
    if (disc) rows.push({ k: o.discount_code ? `Discount (${o.discount_code})` : 'Discount', v: `− ${formatMoney(Math.abs(disc), prefix)}`, tone: 'var(--pg-success, #15803d)' })
    const tax = money(o.tax); if (tax && num(o.tax)) rows.push({ k: 'GST', v: tax })
    const ship = num(o.shipping); if (o.shipping != null && o.shipping !== '') rows.push({ k: 'Shipping', v: ship ? formatMoney(ship, prefix) : 'Free' })
    const total = money(o.total); if (total && rows.length) rows.push({ k: 'Total', v: total, strong: true })
    if (!rows.length && !o.payment_method) return null
    return (
      <div style={{ borderTop: BORDER, paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.9rem' }}>
        {rows.map((r) => (
          <div key={r.k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontWeight: r.strong ? 700 : 400,
                                  fontSize: r.strong ? '1rem' : undefined, color: r.tone }}>
            <span>{r.k}</span><span>{r.v}</span>
          </div>
        ))}
        {o.payment_method && (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: MUTED, fontSize: '0.85rem', marginTop: 4 }}>
            <span>Payment method</span><span>{label(o.payment_method)}</span>
          </div>
        )}
      </div>
    )
  }

  const cards = data.records.map((o) => {
    const total = num(o.total)
    const vars = { id: o.id, reference: o.reference == null ? '' : String(o.reference) }
    const unpaid = payFor(o)
    const failed = String(o.payment_status || '').toUpperCase() === 'FAILED'
    return (
      <article key={o.id} style={CARD}>
        <header style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <strong>{o.reference == null ? '' : String(o.reference)}</strong>
            {o.date && <span style={{ fontSize: '0.8rem', color: MUTED }}>{String(o.date).slice(0, 10)}</span>}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {o.status && <Chip text={label(o.status)} tone={block.statusTones?.[String(o.status)]} />}
            {o.payment_status && <Chip text={label(o.payment_status)} tone={block.statusTones?.[String(o.payment_status)]} />}
            {total !== null && <strong>{formatMoney(total, prefix)}</strong>}
          </div>
        </header>
        {unpaid && (
          <div role="status" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between',
                                      padding: '10px 12px', borderRadius: 10,
                                      background: failed ? 'var(--pg-error-bg, #fef2f2)' : 'var(--pg-warning-bg, #fffbeb)',
                                      color: failed ? 'var(--pg-error, #b91c1c)' : 'var(--pg-warning, #b45309)', fontSize: '0.88rem' }}>
            <span>{failed ? 'Your payment did not go through. The order is saved — you can pay again.' : 'This order is waiting for payment.'}</span>
            <a href={substituteVars(block.pay!.href, vars)} style={{ ...PRIMARY_BTN, padding: '8px 14px', fontSize: '0.85rem', textDecoration: 'none' }}>
              {block.pay!.label || 'Complete payment'}
            </a>
          </div>
        )}
        {o.lines.length > 0 && (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {o.lines.map((l, i) => {
              const href = block.lines?.hrefTemplate && l.sku
                ? substituteVars(block.lines.hrefTemplate, { sku: String(l.sku) }) : ''
              const amount = num(l.amount)
              const unit = num(l.unit_price)
              const name = String(l.name || l.sku || '')
              return (
                <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: '0.9rem' }}>
                  {l.image && <img src={l.image} alt="" width={44} height={44} style={{ objectFit: 'cover', borderRadius: 6, flex: '0 0 auto' }} />}
                  <span style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <span>{href ? <a href={href} style={{ color: 'inherit' }}>{name}</a> : name}</span>
                    <span style={{ color: MUTED, fontSize: '0.82rem' }}>
                      {unit !== null ? `${formatMoney(unit, prefix)} × ${l.qty ?? ''}` : `× ${l.qty ?? ''}`}
                    </span>
                  </span>
                  {amount !== null && <span>{formatMoney(amount, prefix)}</span>}
                </li>
              )
            })}
          </ul>
        )}
        {breakdown(o)}
        {single && o.address && (
          <div style={{ fontSize: '0.88rem' }}>
            <div style={{ color: MUTED, fontSize: '0.8rem', marginBottom: 2 }}>Delivering to</div>
            {String(o.address)}
          </div>
        )}
        {(reorder || block.invoiceHref) && !single && (
          <footer style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {reorder && o.lines.length > 0 && (
              <button type="button" disabled={design} onClick={() => reorderLines(o)}
                      style={{ ...PRIMARY_BTN, padding: '7px 14px', fontSize: '0.85rem' }}>
                {reorder.label || 'Buy again'}
              </button>
            )}
            {block.invoiceHref && (
              <a href={substituteVars(block.invoiceHref, vars)} style={{ alignSelf: 'center', color: 'var(--pg-link, var(--pg-primary))', fontSize: '0.85rem', fontWeight: 600 }}>
                Invoice
              </a>
            )}
          </footer>
        )}
      </article>
    )
  })

  if (single) {
    const o = data.records[0]
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-pg-my-records="">
        {o ? (
          <>
            {(block.singleTitle || block.singleBody) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {block.singleTitle && <h2 style={{ margin: 0, fontSize: '1.35rem' }}>{substituteVars(block.singleTitle, { reference: String(o.reference ?? '') })}</h2>}
                {block.singleBody && <p style={{ margin: 0, color: MUTED }}>{substituteVars(block.singleBody, { reference: String(o.reference ?? '') })}</p>}
              </div>
            )}
            {cards[0]}
          </>
        ) : (
          <div style={{ ...CARD, alignItems: 'center', textAlign: 'center' }}>
            <span>We could not find that order in your account.</span>
            {block.emptyCtaHref && (
              <a href={block.emptyCtaHref} style={{ color: 'var(--pg-link, var(--pg-primary))', fontWeight: 600 }}>
                {block.emptyCtaLabel || 'See my orders'}
              </a>
            )}
          </div>
        )}
        {continueLink}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-pg-my-records="">
      {(block.heading ?? 'My records') && <h2 style={{ margin: 0, fontSize: '1.35rem' }}>{block.heading ?? 'My records'}</h2>}
      {data.records.length === 0 && (
        <div style={{ ...CARD, alignItems: 'center', textAlign: 'center' }}>
          <span>{block.emptyText || 'Nothing here yet.'}</span>
          {block.emptyCtaHref && (
            <a href={block.emptyCtaHref} style={{ color: 'var(--pg-link, var(--pg-primary))', fontWeight: 600 }}>
              {block.emptyCtaLabel || 'Start shopping'}
            </a>
          )}
        </div>
      )}
      {cards}
      {data.records.length < data.total && (
        <button type="button" disabled={loading} onClick={() => load(data.records.length)}
                style={{ alignSelf: 'center', padding: '8px 16px', border: BORDER, borderRadius: 8, background: 'transparent', color: 'inherit', cursor: 'pointer' }}>
          {loading ? 'Loading…' : 'Show more'}
        </button>
      )}
      {data.records.length > 0 && continueLink}
    </div>
  )
}

// ─── saved_records — an account's own address book / payment preferences ──
//
// Cards, not a grid: each saved entry shows what a person recognises (its
// label, the address lines, "UPI · name@bank"), with Edit / Make default /
// Delete on the card and Add in a dialog. Choices come from the SERVER
// (`options`) — for payment methods that is what the project's gateway
// actually accepts — and every write goes back through the block's own door,
// which re-reads the block from the saved page and acts AS the visitor.
// `single` turns it into a one-per-account entry (an application, a
// profile): the form sits inline until it is saved, then a status card.

const SAVED_ICON: Record<string, string> = { address: 'map-pin', payment_method: 'credit-card', generic: 'file-text' }
const SAVED_WORDS: Record<string, { add: string; empty: string; title: string; one: string }> = {
  address: { add: 'Add a new address', empty: 'You have no saved addresses yet.', title: 'Address', one: 'address' },
  payment_method: { add: 'Add a payment method', empty: 'You have no saved payment methods yet.', title: 'Payment method', one: 'payment method' },
  generic: { add: 'Add new', empty: 'Nothing saved yet.', title: 'Details', one: 'entry' },
}

const SAMPLE_SAVED: SavedRecordsResult = {
  records: [{ id: 'sample', values: {}, is_default: true, editable: true }], options: {}, max: 10, has_default: true,
}

function fieldVisible(f: SavedRecordsField, values: Record<string, string>): boolean {
  if (!f.showWhen) return true
  return (f.showWhen.in || []).includes(values[f.showWhen.key] || '')
}

function displayValue(f: SavedRecordsField, v: string, options: SavedRecordsResult['options']): string {
  if (!v) return ''
  if (f.kind === 'select') {
    const opt = (options[f.key] || f.options || []).find((o) => o.value === v)
    return opt ? opt.label : v
  }
  return v
}

function SavedRecordForm({ block, title, options, initial, me, busy, error, fieldErrors, inline, onCancel, onSave }: {
  block: SavedRecordsBlock
  title: string
  options: SavedRecordsResult['options']
  initial: SavedRecord | null
  me?: SavedRecordsResult['me']
  busy: boolean
  error: string
  fieldErrors: Record<string, string>
  /** In the page flow (single entry, nothing saved yet) instead of a dialog. */
  inline?: boolean
  onCancel?: () => void
  onSave: (values: Record<string, string>, makeDefault: boolean) => void
}) {
  const formFields = block.fields.filter((f) => f.inForm !== false)
  // The dialog is portalled to <body> (below), out of the page's stacking
  // context — otherwise the site footer / sticky header, later in the DOM,
  // paints over it. The page's theme lives in --pg-* custom properties on its
  // wrapper, so they are read off an anchor left in place and carried along.
  const anchorRef = useRef<HTMLSpanElement | null>(null)
  const [themeVars, setThemeVars] = useState<Record<string, string>>({})
  useLayoutEffect(() => {
    if (inline || !anchorRef.current || typeof getComputedStyle === 'undefined') return
    const cs = getComputedStyle(anchorRef.current)
    const out: Record<string, string> = {}
    for (let i = 0; i < cs.length; i++) {
      const n = cs[i]
      if (n.startsWith('--pg')) out[n] = cs.getPropertyValue(n)
    }
    const font = cs.getPropertyValue('font-family')
    if (font) out.fontFamily = font
    setThemeVars(out)
  }, [inline])
  const [values, setValues] = useState<Record<string, string>>(() => {
    const v: Record<string, string> = { ...(initial?.values || {}) }
    for (const f of formFields) {
      if (!initial && f.prefill && !v[f.key] && me) {
        const p = me[f.prefill]
        if (p) v[f.key] = String(p)
      }
      // A select from the gateway starts on its first choice, so a dependent
      // field (UPI ID under "UPI") is already in view.
      if (f.kind === 'select' && !v[f.key] && f.optionsFrom) {
        const first = (options[f.key] || [])[0]
        if (first) v[f.key] = first.value
      }
    }
    return v
  })
  const [makeDefault, setMakeDefault] = useState(false)
  const firstRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (inline) return
    firstRef.current?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy && onCancel) onCancel() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [busy, onCancel, inline])
  const set = (k: string, v: string) => setValues((prev) => ({ ...prev, [k]: v }))
  const visible = formFields.filter((f) => fieldVisible(f, values))

  const form = (
    <form role={inline ? undefined : 'dialog'} aria-modal={inline ? undefined : true} aria-label={title}
          onSubmit={(e) => { e.preventDefault(); onSave(values, makeDefault) }}
          style={{ ...CARD, gap: 16, padding: inline ? 20 : 20,
                   ...(inline ? {} : { width: 'min(560px, 100%)', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto',
                                        boxShadow: '0 24px 64px rgba(15,23,42,.25)' }) }}>
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h3 style={{ margin: 0, fontSize: '1.15rem' }}>{title}</h3>
          {block.intro && <span style={{ fontSize: '0.88rem', color: MUTED }}>{block.intro}</span>}
        </div>
        {!inline && onCancel && (
          <button type="button" aria-label="Close" onClick={onCancel} disabled={busy}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 22, lineHeight: 1, color: MUTED }}>×</button>
        )}
      </header>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 12 }}>
        {visible.map((f, i) => {
          const opts = options[f.key] || f.options || []
          const err = fieldErrors[f.key]
          const id = `sr-${block.id}-${f.key}`
          const inputStyle = { ...INPUT, ...(err ? { borderColor: 'var(--pg-error, #dc2626)' } : {}) }
          const focusRef = i === 0 ? (el: HTMLElement | null) => { firstRef.current = el } : undefined
          const asChips = f.kind === 'select' && opts.length > 0 && opts.length <= 6
          return (
            <Fragment key={f.key}>
              {f.group && (
                <div style={{ gridColumn: '1 / -1', marginTop: i === 0 ? 0 : 6, fontSize: '0.78rem', fontWeight: 700,
                              letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED }}>{f.group}</div>
              )}
              <label htmlFor={asChips ? undefined : id}
                     style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.85rem', fontWeight: 600,
                              gridColumn: f.half ? undefined : '1 / -1' }}>
                <span>{f.label}{f.required ? <span style={{ color: 'var(--pg-error, #dc2626)' }}> *</span> : null}</span>
                {f.kind === 'select' ? (
                  opts.length === 0 ? (
                    <span style={{ fontWeight: 400, color: MUTED }}>No options are available yet.</span>
                  ) : asChips ? (
                    <div role="radiogroup" aria-label={f.label} style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {opts.map((o, j) => {
                        const on = values[f.key] === o.value
                        return (
                          <button key={o.value} type="button" role="radio" aria-checked={on}
                                  ref={j === 0 ? focusRef : undefined}
                                  onClick={() => set(f.key, on && !f.required ? '' : o.value)}
                                  style={{ padding: '8px 14px', borderRadius: 999, cursor: 'pointer', fontWeight: 600,
                                           fontSize: '0.86rem', fontFamily: 'inherit',
                                           border: on ? '2px solid var(--pg-primary, #2563eb)' : BORDER,
                                           background: on ? 'color-mix(in srgb, var(--pg-primary, #2563eb) 10%, transparent)' : 'var(--pg-surface, #fff)',
                                           color: 'var(--pg-text, inherit)' }}>
                            {o.label}
                          </button>
                        )
                      })}
                    </div>
                  ) : (
                    <select id={id} required={!!f.required} value={values[f.key] || ''} ref={focusRef}
                            onChange={(e) => set(f.key, e.target.value)} style={inputStyle}>
                      <option value="">Choose…</option>
                      {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  )
                ) : f.kind === 'textarea' ? (
                  <textarea id={id} required={!!f.required} rows={3} value={values[f.key] || ''} maxLength={f.maxLength}
                            placeholder={f.placeholder} autoComplete={f.autocomplete} ref={focusRef}
                            aria-invalid={err ? true : undefined} style={{ ...inputStyle, resize: 'vertical' }}
                            onChange={(e) => set(f.key, e.target.value)} />
                ) : (
                  <input id={id} required={!!f.required}
                         type={f.kind === 'tel' ? 'tel' : f.kind === 'email' ? 'email' : 'text'}
                         inputMode={f.kind === 'postcode' || (f.pattern || '').includes('[0-9]') ? 'numeric' : undefined}
                         value={values[f.key] || ''} maxLength={f.maxLength}
                         placeholder={f.placeholder} autoComplete={f.autocomplete} ref={focusRef}
                         aria-invalid={err ? true : undefined} style={inputStyle}
                         onChange={(e) => set(f.key, e.target.value)} />
                )}
                {err && <span role="alert" style={{ color: 'var(--pg-error, #dc2626)', fontWeight: 500, fontSize: '0.8rem' }}>{err}</span>}
              </label>
            </Fragment>
          )
        })}
      </div>
      {block.defaultColumn && !block.single && !initial?.is_default && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={makeDefault} onChange={(e) => setMakeDefault(e.target.checked)} />
          Use this as my default
        </label>
      )}
      {error && <div role="alert" style={{ color: 'var(--pg-error, #dc2626)', fontSize: '0.88rem' }}>{error}</div>}
      <footer style={{ display: 'flex', gap: 10, justifyContent: inline ? 'flex-start' : 'flex-end', flexWrap: 'wrap' }}>
        {onCancel && (
          <button type="button" onClick={onCancel} disabled={busy}
                  style={{ padding: '10px 18px', border: BORDER, borderRadius: 'var(--pg-button-radius, 8px)', background: 'transparent', color: 'inherit', cursor: 'pointer', fontWeight: 600 }}>
            Cancel
          </button>
        )}
        <button type="submit" disabled={busy} style={{ ...PRIMARY_BTN, opacity: busy ? 0.7 : 1 }}>
          {busy ? 'Saving…' : (initial ? 'Save changes' : (block.submitLabel || 'Save'))}
        </button>
      </footer>
    </form>
  )
  if (inline) return form
  const overlay = (
    <div role="presentation" data-pg-saved-dialog="" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy && onCancel) onCancel() }}
         style={{ ...(themeVars as React.CSSProperties), position: 'fixed', inset: 0, zIndex: 10000,
                  background: 'rgba(15,23,42,.45)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', padding: 16, color: 'var(--pg-text)' }}>
      {form}
    </div>
  )
  return (
    <>
      <span ref={anchorRef} hidden aria-hidden />
      {typeof document === 'undefined' ? overlay : createPortal(overlay, document.body)}
    </>
  )
}

export function SavedRecordsRenderer({ block, design = false }: { block: SavedRecordsBlock; design?: boolean }) {
  const api = useContext(PageDataBlocksContext)
  const live = !design && !!api?.savedList
  const kind = block.kind || 'generic'
  const words = SAVED_WORDS[kind] || SAVED_WORDS.generic
  const [data, setData] = useState<SavedRecordsResult | null>(design ? SAMPLE_SAVED : null)
  const [err, setErr] = useState<unknown>(null)
  const [editing, setEditing] = useState<SavedRecord | 'new' | null>(null)
  const [busy, setBusy] = useState(false)
  const [formErr, setFormErr] = useState('')
  const [fieldErrs, setFieldErrs] = useState<Record<string, string>>({})
  const [confirmDel, setConfirmDel] = useState<string | null>(null)
  const [notice, setNotice] = useState('')
  const [started, setStarted] = useState(false)

  const load = useCallback(() => {
    if (!live) return
    api!.savedList!(block.id).then((d) => { setData(d); setErr(null) }).catch(setErr)
  }, [live, api, block.id])
  useEffect(() => { load() }, [load])
  useEffect(() => {
    if (!notice || block.single) return
    const t = window.setTimeout(() => setNotice(''), 2600)
    return () => window.clearTimeout(t)
  }, [notice, block.single])

  if (!design && !api?.savedList) {
    return <div style={{ ...CARD, color: MUTED, fontSize: 13 }}>This section is not available here.</div>
  }
  if (err) {
    if (isSignInError(err)) return <SignInSlot api={api} code={err.code} message={err.message} />
    return <div role="alert" style={{ ...CARD, color: 'var(--pg-error, #dc2626)' }}>{messageOf(err, 'Your saved details could not be loaded.')}</div>
  }
  if (!data) return <div style={{ ...CARD, color: MUTED }}>Loading…</div>

  const options = data.options || {}
  const titleFields = block.fields.filter((f) => f.role === 'title')
  const lineFields = block.fields.filter((f) => f.role === 'line')
  const metaFields = block.fields.filter((f) => f.role === 'meta')
  const statusField = block.fields.find((f) => f.role === 'status')
  const formTitle = block.formTitle || words.title
  const canDelete = data.allow_delete !== false && block.allowDelete !== false

  const resetForm = () => { setFormErr(''); setFieldErrs({}) }
  const save = async (values: Record<string, string>, makeDefault: boolean) => {
    if (!live) return
    setBusy(true); resetForm()
    try {
      const id = editing && editing !== 'new' ? editing.id : undefined
      await api!.savedSave!(block.id, { id, values, make_default: makeDefault })
      notifyRecordsChanged(block.object)
      setEditing(null)
      setNotice(id ? 'Saved your changes.' : (block.successText || `Saved your new ${words.one}.`))
      load()
    } catch (e) {
      const details = (e as { details?: { fields?: Record<string, string> } })?.details
      if (details?.fields) setFieldErrs(details.fields)
      setFormErr(messageOf(e, 'Could not save this.'))
    } finally { setBusy(false) }
  }
  const makeDefault = async (id: string) => {
    if (!live) return
    setBusy(true)
    try { await api!.savedDefault!(block.id, id); notifyRecordsChanged(block.object); setNotice('Default updated.'); load() }
    catch (e) { setNotice(messageOf(e, 'Could not change the default.')) }
    finally { setBusy(false) }
  }
  const remove = async (id: string) => {
    if (!live) return
    setBusy(true)
    try { await api!.savedDelete!(block.id, id); notifyRecordsChanged(block.object); setConfirmDel(null); setNotice('Deleted.'); load() }
    catch (e) { setNotice(messageOf(e, 'Could not delete this.')) }
    finally { setBusy(false) }
  }

  const cardLines = (r: SavedRecord) => {
    const lines: string[] = []
    for (const f of lineFields) {
      if (!fieldVisible(f, r.values)) continue
      const v = displayValue(f, r.values[f.key] || '', options)
      if (!v) continue
      if (f.joinPrevious && lines.length) lines[lines.length - 1] += `, ${v}`
      else lines.push(v)
    }
    return lines
  }
  const linkBtn: React.CSSProperties = {
    border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontWeight: 600,
    fontSize: '0.85rem', color: 'var(--pg-link, var(--pg-primary, #2563eb))', fontFamily: 'inherit',
  }

  // One entry per account, none yet: the form IS the block — behind a
  // start button when the author asked for one ("Apply").
  if (block.single && data.records.length === 0 && !design && block.startLabel && !started) {
    return (
      <div data-pg-saved-records="" data-pg-saved-start="" style={{ ...CARD, gap: 12, alignItems: 'flex-start' }}>
        {block.heading && <h2 style={{ margin: 0, fontSize: '1.35rem' }}>{block.heading}</h2>}
        {block.startText && <p style={{ margin: 0, color: MUTED, lineHeight: 1.5 }}>{block.startText}</p>}
        <button type="button" onClick={() => { resetForm(); setStarted(true) }}
                style={{ border: 'none', borderRadius: 'var(--pg-button-radius, 8px)', padding: '12px 20px', cursor: 'pointer',
                         background: 'var(--pg-primary, #2563eb)', color: 'var(--pg-on-primary, #fff)',
                         fontWeight: 600, fontSize: '0.95rem', fontFamily: 'inherit' }}>
          {block.startLabel}
        </button>
      </div>
    )
  }
  if (block.single && data.records.length === 0 && !design) {
    return (
      <div data-pg-saved-records="" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {block.heading && <h2 style={{ margin: 0, fontSize: '1.35rem' }}>{block.heading}</h2>}
        <SavedRecordForm block={block} title={formTitle} options={options} initial={null} me={data.me}
                         busy={busy} error={formErr} fieldErrors={fieldErrs} inline onSave={save}
                         onCancel={block.startLabel ? () => setStarted(false) : undefined} />
      </div>
    )
  }

  const atMax = data.records.length >= (data.max || Infinity)
  return (
    <div data-pg-saved-records="" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {block.heading && <h2 style={{ margin: 0, fontSize: '1.35rem' }}>{block.heading}</h2>}
      {notice && (
        <div aria-live="polite" style={{ ...CARD, flexDirection: 'row', alignItems: 'center', gap: 10, padding: '12px 14px',
                                          background: 'var(--pg-success-bg, #ecfdf5)', borderColor: 'transparent',
                                          color: 'var(--pg-success, #15803d)', fontSize: '0.9rem', fontWeight: 600 }}>
          <PageIcon name="check-circle" size={18} />{notice}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: block.single ? '1fr' : 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))', gap: 14 }}>
        {data.records.map((r) => {
          const title = design
            ? (kind === 'address' ? 'Home' : kind === 'payment_method' ? 'UPI' : formTitle)
            : (titleFields.map((f) => displayValue(f, r.values[f.key] || '', options)).filter(Boolean).join(' · ') || words.title)
          const lines = design
            ? (kind === 'address' ? ['Dr. A. Sharma', '12 MG Road, Indiranagar', 'Bengaluru, Karnataka 560038'] : ['name@okbank'])
            : cardLines(r)
          const metas = design ? [] : metaFields.map((f) => displayValue(f, r.values[f.key] || '', options)).filter(Boolean)
          const status = statusField ? (r.values[statusField.key] || '') : ''
          const canEdit = r.editable !== false
          return (
            <article key={r.id} style={{ ...CARD, gap: 10, position: 'relative',
                                         ...(r.is_default && !block.single ? { borderColor: 'var(--pg-primary, #2563eb)', boxShadow: '0 0 0 1px var(--pg-primary, #2563eb)' } : {}) }}>
              <header style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span aria-hidden style={{ width: 34, height: 34, borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                           background: 'color-mix(in srgb, var(--pg-primary, #2563eb) 10%, transparent)', color: 'var(--pg-primary, #2563eb)', flex: '0 0 auto' }}>
                  <PageIcon name={SAVED_ICON[kind] || 'file-text'} size={18} />
                </span>
                <strong style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</strong>
                {status && <Chip text={block.statusLabels?.[status] || status} tone={block.statusTones?.[status]} />}
                {r.is_default && !block.single && <Chip text="Default" tone="success" />}
              </header>
              {lines.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '0.9rem', lineHeight: 1.45 }}>
                  {lines.map((l, i) => <span key={i}>{l}</span>)}
                </div>
              )}
              {metas.length > 0 && <div style={{ fontSize: '0.82rem', color: MUTED }}>{metas.join(' · ')}</div>}
              {status && block.statusNotes?.[status] && (
                <p data-pg-saved-status-note={status} style={{ margin: 0, fontSize: '0.9rem', lineHeight: 1.5 }}>
                  {block.statusNotes[status]}
                </p>
              )}
              {(canEdit || canDelete || (data.has_default && !r.is_default)) && (
                <footer style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginTop: 'auto', paddingTop: 4 }}>
                  {confirmDel === r.id ? (
                    <>
                      <span style={{ fontSize: '0.85rem' }}>Delete this {words.one}?</span>
                      <button type="button" disabled={busy} onClick={() => remove(r.id)} style={{ ...linkBtn, color: 'var(--pg-error, #dc2626)' }}>Delete</button>
                      <button type="button" disabled={busy} onClick={() => setConfirmDel(null)} style={{ ...linkBtn, color: MUTED }}>Keep</button>
                    </>
                  ) : (
                    <>
                      {canEdit && <button type="button" disabled={design || busy} onClick={() => { resetForm(); setEditing(r) }} style={linkBtn}>Edit</button>}
                      {data.has_default && !r.is_default && (
                        <button type="button" disabled={design || busy} onClick={() => makeDefault(r.id)} style={linkBtn}>Make default</button>
                      )}
                      {canDelete && <button type="button" disabled={design || busy} onClick={() => setConfirmDel(r.id)} style={{ ...linkBtn, color: MUTED }}>Delete</button>}
                    </>
                  )}
                </footer>
              )}
            </article>
          )
        })}
        {!atMax && !block.single && (
          <button type="button" disabled={design} onClick={() => { resetForm(); setEditing('new') }}
                  style={{ ...CARD, alignItems: 'center', justifyContent: 'center', minHeight: 140, cursor: 'pointer',
                           borderStyle: 'dashed', color: 'var(--pg-primary, #2563eb)', fontWeight: 600, fontFamily: 'inherit', fontSize: '0.95rem' }}>
            <PageIcon name="plus-circle" size={22} />
            {block.addLabel || words.add}
          </button>
        )}
      </div>
      {data.records.length === 0 && (
        <span style={{ color: MUTED, fontSize: '0.9rem' }}>{block.emptyText || words.empty}</span>
      )}
      {editing && (
        <SavedRecordForm block={block}
                         title={editing === 'new' ? (block.addLabel || words.add) : `Edit ${formTitle.toLowerCase()}`}
                         options={options} initial={editing === 'new' ? null : editing} me={data.me}
                         busy={busy} error={formErr} fieldErrors={fieldErrs}
                         onCancel={() => setEditing(null)} onSave={save} />
      )}
    </div>
  )
}
